import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import ApiService from 'services/apiService';
import { useDashboardSchoolYear } from './DashboardSchoolYearContext';
import { GPASubmissionFilter, GPASubmissionOption, GPASubmissionResponse, GPASubmissionRow, GPASubmissionTermType } from '../GPASubmissionDashboard/types';

interface Value {
    data: GPASubmissionRow[];
    loading: boolean;
    error: string | null;
    filter: GPASubmissionFilter;
    activeSchoolYear: number | null;
    schools: GPASubmissionOption[];
    gradeLevels: GPASubmissionOption[];
    termTypes: GPASubmissionTermType[];
    updateFilter: (updates: Partial<GPASubmissionFilter>) => void;
}

const Context = createContext<Value | undefined>(undefined);

export const GPASubmissionDataProvider = ({ children }: { children: ReactNode }) => {
    const { schoolYear } = useDashboardSchoolYear();
    const [filter, setFilter] = useState<GPASubmissionFilter>({ schoolYears: [], schoolIds: [], gradeLevelIds: [], termTypeIds: [], termNumbers: [] });
    const [data, setData] = useState<GPASubmissionRow[]>([]);
    const [schools, setSchools] = useState<GPASubmissionOption[]>([]);
    const [gradeLevels, setGradeLevels] = useState<GPASubmissionOption[]>([]);
    const [termTypes, setTermTypes] = useState<GPASubmissionTermType[]>([]);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => { if (schoolYear) setFilter(previous => ({ ...previous, schoolYears: [schoolYear] })); }, [schoolYear]);
    useEffect(() => {
        let current = true;
        setLoading(true);
        setError(null);
        ApiService.instance.post<GPASubmissionResponse>('/GPADashboard/GetGPASubmissionRates', filter)
            .then(response => {
                if (!current) return;
                setData(response.data.data || []);
                setSchools(response.data.schools || []);
                setGradeLevels(response.data.gradeLevels || []);
                setTermTypes(response.data.termTypes || []);
                setActiveSchoolYear(response.data.activeSchoolYear);
                if (!filter.schoolYears.length && response.data.activeSchoolYear)
                    setFilter(previous => ({ ...previous, schoolYears: [response.data.activeSchoolYear] }));
            })
            .catch(() => { if (current) { setError('Failed to load GPA submission data'); setData([]); } })
            .finally(() => { if (current) setLoading(false); });
        return () => { current = false; };
    }, [filter]);

    return <Context.Provider value={{ data, loading, error, filter, activeSchoolYear, schools, gradeLevels, termTypes,
        updateFilter: updates => setFilter(previous => ({ ...previous, ...updates })) }}>{children}</Context.Provider>;
};

export const useGPASubmissionData = () => {
    const value = useContext(Context);
    if (!value) throw new Error('useGPASubmissionData must be used within GPASubmissionDataProvider');
    return value;
};
